'use client';

import { Subject } from '@/hooks/useSubject';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { useStrings } from '@/resources/translations/translations';
import { slugify } from '@/utils/strings';
import { mdiArrowLeftCircle, mdiArrowRightCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { useLayoutEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const PageNavigationButtons = ({
    subject,
    pageId,
}: {
    subject: Subject;
    pageId: number;
}) => {
    const strings = useStrings('components.PageNavigationButtons');

    const allPageIdsSorted = useMemo(
        () =>
            subject.chapters.reduce(
                (acc, chapter) => [...acc, ...chapter.page_ids],
                [] as number[],
            ),
        [subject],
    );

    const previousPage: PageNavigationButtonProps = useMemo<PageNavigationButtonProps>(() => {
        const index = allPageIdsSorted.findIndex((p) => p === pageId);
        const previousPageId = allPageIdsSorted[index - 1];

        return {
            subject,
            pageId: previousPageId,
            navigationDirection: -1,
            enableBasedOn: 'back_stack',
        };
    }, [subject, allPageIdsSorted, pageId]);

    const nextPage: PageNavigationButtonProps = useMemo<PageNavigationButtonProps>(() => {
        const index = allPageIdsSorted.findIndex((p) => p === pageId);
        const nextPageId = allPageIdsSorted[index + 1];

        return {
            subject,
            pageId: nextPageId,
            navigationDirection: 1,
            enableBasedOn: 'progress',
        };
    }, [subject, allPageIdsSorted, pageId]);

    return (
        <nav aria-label={strings('navigation_label')}>
            <PageNavigationButton {...previousPage} />
            <PageNavigationButton {...nextPage} />
        </nav>
    );
};

type PageNavigationButtonProps = {
    subject: Subject;
    pageId?: number;
    navigationDirection: -1 | 1;
    enableBasedOn: 'back_stack' | 'progress';
};

const PageNavigationButton = ({
    subject,
    pageId,
    navigationDirection,
    enableBasedOn,
}: PageNavigationButtonProps) => {
    const strings = useStrings('components.PageNavigationButtons');
    const reduceMotion = useReducedMotion();
    const { subjectProgress } = useSubjectProgress(subject.id);

    const [isEnabled, setIsEnabled] = useState(false);

    useLayoutEffect(() => {
        // If based on progress, check if the page to navigate to has already been visited
        if (
            enableBasedOn === 'progress' &&
            Object.keys(subjectProgress?.pages ?? {}).includes(`${pageId}`)
        ) {
            setIsEnabled(true);
        }

        // If based on back stack, check if this page is in the back stack
        if (
            enableBasedOn === 'back_stack' &&
            navigationDirection === -1 &&
            pageId
        ) {
            setIsEnabled(true);
        }
    }, [subjectProgress, pageId]);

    const chapter = subject.chapters.find((c) =>
        c.page_ids.includes(pageId ?? 0),
    );

    if (!pageId || !chapter) return null;

    return (
        <AnimatePresence>
            {isEnabled && (
                <motion.div
                    initial={{
                        opacity: 0,
                        scale: reduceMotion ? 1 : 0,
                    }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                    }}
                    exit={{
                        opacity: 0,
                        scale: reduceMotion ? 1 : 0,
                    }}
                    whileHover={{
                        scale: !reduceMotion ? 1.1 : 1,
                        opacity: 0.9,
                    }}
                    whileTap={{
                        scale: 1,
                        opacity: 0.8,
                    }}
                    className={twMerge(
                        'absolute top-2 md:top-1/2 z-100',
                        navigationDirection === -1
                            ? 'left-2 md:-left-6 xl:-left-20'
                            : 'right-2 md:-right-6 xl:-right-20',
                        'w-10 h-10 md:w-12 md:h-12 xl:w-16 xl:h-16',
                    )}
                >
                    <Link
                        href={`/story/${subject.id}/${slugify(subject.title)}/${
                            chapter.id
                        }/${slugify(chapter.title)}/${pageId}`}
                        passHref
                        aria-label={
                            navigationDirection === -1
                                ? strings('back')
                                : strings('forward')
                        }
                        title={
                            navigationDirection === -1
                                ? strings('back')
                                : strings('forward')
                        }
                        className={twMerge(
                            'flex justify-center items-center w-full h-full',
                            'max-xl:bg-gradient-to-b from-secondary-text-base-aa to-secondary-800 text-white',
                            'rounded-full max-xl:shadow-lg transition-all duration-300',
                        )}
                    >
                        <Icon
                            path={
                                navigationDirection === -1
                                    ? mdiArrowLeftCircle
                                    : mdiArrowRightCircle
                            }
                            className="w-8 h-8 md:w-10 md:h-10 xl:w-12 xl:h-12"
                        />
                    </Link>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PageNavigationButtons;
